import React, { useEffect, useMemo, useState } from 'react';

import {
  ETH,
} from '../economics/ecoShared';

import type {
  RtaPoint,
  RtaLinearFit,
} from './rtaCalculations';

import {
  buildPseudoPressureRtaPoints,
  cleanGasPvt,
  fitPseudoPressureLinearTrend,
} from './rtaPvtCalculations';

import type {
  GasPvtPoint,
  PseudoPressureRtaPoint,
} from './rtaPvtCalculations';

interface PvtInputRow {
  id: number;
  pressure: string;
  z: string;
  viscosity: string;
}

export default function RtaPseudopressurePanel({ projectId, points, fitPoints, onEngineeringFitChange }: { projectId: number; points: RtaPoint[]; fitPoints: RtaPoint[]; onEngineeringFitChange?: (result: { coverageOk: boolean; fit: RtaLinearFit | null }) => void }) {
  const storageKey = `production_forecast_pvt_${projectId}`;

  const [rows, setRows] = useState<PvtInputRow[]>(() => {
    try {
      const saved = localStorage.getItem(storageKey);
      return saved ? JSON.parse(saved) : makeEmptyRows();
    } catch {
      return makeEmptyRows();
    }
  });

  useEffect(() => {
    try {
      localStorage.setItem(storageKey, JSON.stringify(rows));
    } catch {
      // localStorage不可用时不影响当前页面使用
    }
  }, [rows, storageKey]);
  
  useEffect(() => {
    try {
      const saved = localStorage.getItem(storageKey);
      setRows(saved ? JSON.parse(saved) : makeEmptyRows());
    } catch {
      setRows(makeEmptyRows());
    }
  }, [storageKey]);
  
  const pvt =
    useMemo<
      GasPvtPoint[]
    >(() => {
      return cleanGasPvt(
        rows
          .map(
            (row) => ({
              pressure:
                Number(
                  row.pressure,
                ),

              z:
                Number(
                  row.z,
                ),

              viscosity:
                Number(
                  row.viscosity,
                ),
            }),
          )
          .filter(
            (point) =>
              point.pressure >=
                0 &&
              point.z > 0 &&
              point.viscosity >
                0 &&
              Number.isFinite(
                point.pressure,
              ) &&
              Number.isFinite(
                point.z,
              ) &&
              Number.isFinite(
                point.viscosity,
              ),
          ),
      );
    }, [rows]);

  const pseudoPoints =
    useMemo(
      () =>
        buildPseudoPressureRtaPoints(
          points,
          pvt,
        ),
      [points, pvt],
    );

  const selectedKeys =
    useMemo(
      () =>
        new Set(
          fitPoints.map(
            (point) =>
              `${point.date}|${point.t}`,
          ),
        ),
      [fitPoints],
    );

  const pseudoFitPoints =
    useMemo(
      () =>
        pseudoPoints.filter(
          (point) =>
            selectedKeys.has(
              `${point.date}|${point.t}`,
            ),
        ),
      [
        pseudoPoints,
        selectedKeys,
      ],
    );

  const sqrtTimeFit =
    useMemo(
      () =>
        fitPseudoPressureLinearTrend(
          pseudoFitPoints,
          'sqrtT',
        ),
      [pseudoFitPoints],
    );

  const sqrtMbTimeFit =
    useMemo(
      () =>
        fitPseudoPressureLinearTrend(
          pseudoFitPoints,
          'sqrtTmb',
        ),
      [pseudoFitPoints],
    );

  const requiredMinPressure =
    points.length > 0
      ? Math.min(
          ...points.map(
            (point) =>
              point.pwf,
          ),
        )
      : NaN;

  const requiredMaxPressure =
    points.length > 0
      ? Math.max(
          ...points.map(
            (point) =>
              point.pr,
          ),
        )
      : NaN;

  const pvtMin =
    pvt.length > 0
      ? pvt[0].pressure
      : NaN;

  const pvtMax =
    pvt.length > 0
      ? pvt[
          pvt.length - 1
        ].pressure
      : NaN;

  const coverageOk =
    pvt.length >= 2 &&
    Number.isFinite(
      requiredMinPressure,
    ) &&
    Number.isFinite(
      requiredMaxPressure,
    ) &&
    pvtMin <=
      requiredMinPressure &&
    pvtMax >=
      requiredMaxPressure;

  useEffect(() => {
    onEngineeringFitChange?.({ coverageOk, fit: sqrtTimeFit });
  }, [coverageOk, sqrtTimeFit, onEngineeringFitChange]);
  
      const updateRow = (
    id: number,
    key:
      | 'pressure'
      | 'z'
      | 'viscosity',
    value: string,
  ) => {
    setRows(
      (previous) =>
        previous.map(
          (row) =>
            row.id === id
              ? {
                  ...row,
                  [key]:
                    value,
                }
              : row,
        ),
    );
  };

  const addRow = () => {
    setRows(
      (previous) => [
        ...previous,
        {
          id:
            Date.now(),
          pressure: '',
          z: '',
          viscosity: '',
        },
      ],
    );
  };

  const deleteRow = (
    id: number,
  ) => {
    setRows(
      (previous) =>
        previous.length <= 2
          ? previous
          : previous.filter(
              (row) =>
                row.id !== id,
            ),
    );
  };

  const loadDemoPvt =
    () => {
      /*
       * 仅用于验证程序链路：
       * Z = 1
       * μ = 0.02 mPa·s
       *
       * 不代表真实储层PVT。
       */
      setRows([
        {
          id: 1,
          pressure: '40',
          z: '1',
          viscosity:
            '0.02',
        },
        {
          id: 2,
          pressure: '45',
          z: '1',
          viscosity:
            '0.02',
        },
        {
          id: 3,
          pressure: '50',
          z: '1',
          viscosity:
            '0.02',
        },
        {
          id: 4,
          pressure: '55',
          z: '1',
          viscosity:
            '0.02',
        },
        {
          id: 5,
          pressure: '60',
          z: '1',
          viscosity:
            '0.02',
        },
      ]);
    };

  const clearPvt = () => {
    setRows(
      makeEmptyRows(),
    );
  };

  return (
    <div
      style={{
        marginTop: 14,
      }}
    >
      <div
        style={{
          marginBottom: 10,
        }}
      >
        <div
          style={{
            fontSize: 15,
            fontWeight: 700,
          }}
        >
          实气拟压力 RTA
        </div>

        <div
          style={{
            marginTop: 4,
            fontSize: 11,
            color:
              ETH.textMuted,
          }}
        >
          基于 Z 因子与气体黏度计算
          m(p)，用于替代压力平方近似开展气井
          RTA 解释
        </div>
      </div>

      {/* PVT输入 */}
      <div
        style={{
          padding: 14,
          background:
            ETH.canvasBg,
          border: `1px solid ${ETH.headerBorder}`,
        }}
      >
        <div
          style={{
            display: 'flex',
            justifyContent:
              'space-between',
            alignItems:
              'center',
            gap: 12,
            marginBottom: 10,
          }}
        >
          <div>
            <div
              style={{
                fontSize: 14,
                fontWeight: 700,
              }}
            >
              气体 PVT 数据
            </div>

            <div
              style={{
                marginTop: 4,
                fontSize: 10,
                color:
                  ETH.textMuted,
              }}
            >
              输入压力、Z 因子和对应气体黏度
            </div>
          </div>

          <div
            style={{
              display: 'flex',
              gap: 8,
            }}
          >
            <button
              type="button"
              onClick={
                loadDemoPvt
              }
              style={
                buttonStyle
              }
            >
              填入自检 PVT
            </button>

            <button
              type="button"
              onClick={
                clearPvt
              }
              style={
                buttonStyle
              }
            >
              清空
            </button>

            <button
              type="button"
              onClick={
                addRow
              }
              style={
                buttonStyle
              }
            >
              + 新增 PVT 点
            </button>
          </div>
        </div>

        <div
          style={{
            overflowX:
              'auto',
          }}
        >
          <table
            style={{
              width: '100%',
              borderCollapse:
                'collapse',
              fontSize: 11,
            }}
          >
            <thead>
              <tr>
                <th style={thStyle}>
                  压力 / MPa
                </th>

                <th style={thStyle}>
                  Z 因子
                </th>

                <th style={thStyle}>
                  气体黏度 /
                  mPa·s
                </th>

                <th style={thStyle}>
                  操作
                </th>
              </tr>
            </thead>

            <tbody>
              {rows.map(
                (row) => (
                  <tr
                    key={
                      row.id
                    }
                  >
                    <td
                      style={
                        tdStyle
                      }
                    >
                      <input
                        type="number"
                        value={
                          row.pressure
                        }
                        onChange={(
                          event,
                        ) =>
                          updateRow(
                            row.id,
                            'pressure',
                            event
                              .target
                              .value,
                          )
                        }
                        style={
                          inputStyle
                        }
                      />
                    </td>

                    <td
                      style={
                        tdStyle
                      }
                    >
                      <input
                        type="number"
                        value={
                          row.z
                        }
                        onChange={(
                          event,
                        ) =>
                          updateRow(
                            row.id,
                            'z',
                            event
                              .target
                              .value,
                          )
                        }
                        style={
                          inputStyle
                        }
                      />
                    </td>

                    <td
                      style={
                        tdStyle
                      }
                    >
                      <input
                        type="number"
                        value={
                          row.viscosity
                        }
                        onChange={(
                          event,
                        ) =>
                          updateRow(
                            row.id,
                            'viscosity',
                            event
                              .target
                              .value,
                          )
                        }
                        style={
                          inputStyle
                        }
                      />
                    </td>

                    <td
                      style={{
                        ...tdStyle,
                        textAlign:
                          'center',
                      }}
                    >
                      <button
                        type="button"
                        onClick={() =>
                          deleteRow(
                            row.id,
                          )
                        }
                        disabled={
                          rows.length <=
                          2
                        }
                        style={
                          textButtonStyle
                        }
                      >
                        删除
                      </button>
                    </td>
                  </tr>
                ),
              )}
            </tbody>
          </table>
        </div>

        <div
          style={{
            marginTop: 12,
            display: 'grid',
            gridTemplateColumns:
              'repeat(3, minmax(0, 1fr))',
            gap: 12,
          }}
        >
          <StatusCard
            title="有效PVT点"
            value={`${pvt.length} 个`}
            description="已通过压力、Z、黏度检查"
          />

          <StatusCard
            title="RTA需要压力范围"
            value={
              Number.isFinite(
                requiredMinPressure,
              )
                ? `${fmt(
                    requiredMinPressure,
                    2,
                  )}–${fmt(
                    requiredMaxPressure,
                    2,
                  )} MPa`
                : '--'
            }
            description="Pwf最低值至Pr"
          />

          <StatusCard
            title="PVT覆盖状态"
            value={
              coverageOk
                ? '已覆盖'
                : '未覆盖'
            }
            description={
              coverageOk
                ? '可开展拟压力积分'
                : 'PVT压力范围必须覆盖全部Pwf至Pr'
            }
          />
        </div>
      </div>

      {!coverageOk ? (
        <div
          style={{
            marginTop: 14,
            padding: 28,
            textAlign:
              'center',
            background:
              ETH.canvasBg,
            border: `1px solid ${ETH.headerBorder}`,
          }}
        >
          <div
            style={{
              fontSize: 14,
              fontWeight: 700,
            }}
          >
            等待完整 PVT 数据
          </div>

          <div
            style={{
              marginTop: 8,
              color:
                ETH.textMuted,
              fontSize: 11,
            }}
          >
            PVT 压力范围必须覆盖
            当前全部井底流压 Pwf
            到地层压力 Pr。
          </div>
        </div>
      ) : (
        <>
          {/* 拟合结果 */}
          <div
            style={{
              marginTop: 14,
              display: 'grid',
              gridTemplateColumns:
                '1fr 1fr',
              gap: 14,
            }}
          >
            <FitCard
              title="拟压力 √t 诊断"
              equation="Y = Δm/q，X = √t"
              fit={
                sqrtTimeFit
              }
            />

            <FitCard
              title="拟压力 √tmb 诊断"
              equation="Y = Δm/q，X = √(Gp/q)"
              fit={
                sqrtMbTimeFit
              }
            />
          </div>

          <div
            style={{
              marginTop: 14,
              padding: 14,
              background:
                ETH.canvasBg,
              border: `1px solid ${ETH.headerBorder}`,
            }}
          >
            <div
              style={{
                fontSize: 14,
                fontWeight: 700,
              }}
            >
              实气拟压力线性流诊断图
            </div>

            <div
              style={{
                marginTop: 4,
                fontSize: 11,
                color:
                  ETH.textMuted,
              }}
            >
              黑点为有效 RTA
              数据，红线为当前选定流动阶段的
              Δm/q − √t
              线性拟合
            </div>

            <PseudoPressureChart
              points={
                pseudoPoints
              }
              fitPoints={
                pseudoFitPoints
              }
              fit={
                sqrtTimeFit
              }
            />
          </div>

          {/* 明细 */}
          <div
            style={{
              marginTop: 14,
              padding: 14,
              background:
                ETH.canvasBg,
              border: `1px solid ${ETH.headerBorder}`,
            }}
          >
            <div
              style={{
                fontSize: 14,
                fontWeight: 700,
                marginBottom: 10,
              }}
            >
              拟压力计算明细
            </div>

            <div
              style={{
                overflowX:
                  'auto',
              }}
            >
              <table
                style={{
                  width: '100%',
                  minWidth: 900,
                  borderCollapse:
                    'collapse',
                  fontSize: 11,
                }}
              >
                <thead>
                  <tr>
                    <th style={thStyle}>
                      日期
                    </th>

                    <th style={thStyle}>
                      q /
                      10⁴m³/d
                    </th>

                    <th style={thStyle}>
                      Pr /
                      MPa
                    </th>

                    <th style={thStyle}>
                      Pwf /
                      MPa
                    </th>

                    <th style={thStyle}>
                      Δm
                    </th>

                    <th style={thStyle}>
                      Δm/q
                    </th>

                    <th style={thStyle}>
                      √t
                    </th>

                    <th style={thStyle}>
                      √tmb
                    </th>
                  </tr>
                </thead>

                <tbody>
                  {pseudoPoints.map(
                    (
                      point,
                      index,
                    ) => (
                      <tr
                        key={`${point.date}-${index}`}
                      >
                        <td
                          style={
                            tdStyle
                          }
                        >
                          {
                            point.date
                          }
                        </td>

                        <td
                          style={
                            numberTdStyle
                          }
                        >
                          {fmt(
                            point.q,
                            3,
                          )}
                        </td>

                        <td
                          style={
                            numberTdStyle
                          }
                        >
                          {fmt(
                            point.pr,
                            3,
                          )}
                        </td>

                        <td
                          style={
                            numberTdStyle
                          }
                        >
                          {fmt(
                            point.pwf,
                            3,
                          )}
                        </td>

                        <td
                          style={
                            numberTdStyle
                          }
                        >
                          {fmt(
                            point.deltaM,
                            3,
                          )}
                        </td>

                        <td
                          style={
                            numberTdStyle
                          }
                        >
                          {fmt(
                            point.inverseNormalizedPseudoPressure,
                            4,
                          )}
                        </td>

                        <td
                          style={
                            numberTdStyle
                          }
                        >
                          {fmt(
                            point.sqrtT,
                            3,
                          )}
                        </td>

                        <td
                          style={
                            numberTdStyle
                          }
                        >
                          {fmt(
                            point.sqrtTmb,
                            3,
                          )}
                        </td>
                      </tr>
                    ),
                  )}
                </tbody>
              </table>
            </div>
          </div>

          <div
            style={{
              marginTop: 14,
              padding: 14,
              background:
                ETH.canvasBg,
              border: `1px solid ${ETH.headerBorder}`,
              color:
                ETH.textMuted,
              fontSize: 11,
              lineHeight: 1.8,
            }}
          >
            <div>
              · 实气拟压力定义为
              m(p)=2∫p/(μgZ)dp。
            </div>

            <div>
              · 现场正式解释时应录入实验室或经过校核的
              PVT 数据，不使用“自检PVT”作为工程参数。
            </div>

            <div>
              · 当前仍沿用上方选定的
              RTA 拟合起止日期，因此压力平方法与拟压力法可以直接对照。
            </div>

            <div>
              · 完成拟压力校正后，
              下一阶段再根据线性流段、储层参数和裂缝参数进行
              SRV、裂缝控制尺度与导流能力敏感性解释。
            </div>
          </div>
        </>
      )}
    </div>
  );
}

function PseudoPressureChart({
  points,
  fitPoints,
  fit,
}: {
  points:
    PseudoPressureRtaPoint[];

  fitPoints:
    PseudoPressureRtaPoint[];

  fit:
    RtaLinearFit | null;
}) {
  const width = 900;
  const height = 350;

  const left = 74;
  const right = 30;
  const top = 25;
  const bottom = 52;

  const plotW =
    width -
    left -
    right;

  const plotH =
    height -
    top -
    bottom;

  const xs =
    points.map(
      (point) =>
        point.sqrtT,
    );

  const ys =
    points.map(
      (point) =>
        point.inverseNormalizedPseudoPressure,
    );

  if (
    xs.length === 0 ||
    ys.length === 0
  ) {
    return (
      <div
        style={{
          padding: 30,
          textAlign:
            'center',
          color:
            ETH.textMuted,
        }}
      >
        暂无有效拟压力数据
      </div>
    );
  }

  const maxX =
    Math.max(
      1,
      ...xs,
    ) * 1.05;

  const rawMinY =
    Math.min(...ys);

  const rawMaxY =
    Math.max(...ys);

  const spanY =
    Math.max(
      rawMaxY -
        rawMinY,
      1,
    );

  const minY =
    Math.max(
      0,
      rawMinY -
        0.08 *
          spanY,
    );

  const maxY =
    rawMaxY +
    0.08 *
      spanY;

  const x = (
    value: number,
  ) =>
    left +
    (
      value /
      maxX
    ) *
      plotW;

  const y = (
    value: number,
  ) =>
    top +
    (
      1 -
      (
        value -
        minY
      ) /
        (
          maxY -
          minY
        )
    ) *
      plotH;

  const selectedKeys =
    new Set(
      fitPoints.map(
        (point) =>
          `${point.date}|${point.t}`,
      ),
    );

  return (
    <div
      style={{
        marginTop: 12,
        overflowX:
          'auto',
      }}
    >
      <svg
        viewBox={`0 0 ${width} ${height}`}
        style={{
          width: '100%',
          minWidth: 720,
          height,
          display: 'block',
        }}
      >
        <rect
          width={width}
          height={height}
          fill={ETH.bg}
        />

        {Array.from(
          {
            length: 6,
          },
          (_, index) =>
            minY +
            (
              maxY -
              minY
            ) *
              index /
              5,
        ).map(
          (value) => (
            <g key={value}>
              <line
                x1={left}
                x2={
                  width -
                  right
                }
                y1={y(value)}
                y2={y(value)}
                stroke={
                  ETH.headerBorder
                }
              />

              <text
                x={
                  left - 8
                }
                y={
                  y(value) +
                  4
                }
                textAnchor="end"
                fontSize={10}
                fill={
                  ETH.textMuted
                }
              >
                {value.toFixed(
                  1,
                )}
              </text>
            </g>
          ),
        )}

        {Array.from(
          {
            length: 6,
          },
          (_, index) =>
            maxX *
            index /
            5,
        ).map(
          (value) => (
            <g key={value}>
              <line
                x1={x(value)}
                x2={x(value)}
                y1={top}
                y2={
                  height -
                  bottom
                }
                stroke={
                  ETH.headerBorder
                }
              />

              <text
                x={x(value)}
                y={
                  height -
                  bottom +
                  18
                }
                textAnchor="middle"
                fontSize={10}
                fill={
                  ETH.textMuted
                }
              >
                {value.toFixed(
                  1,
                )}
              </text>
            </g>
          ),
        )}

        {fit && (
          <line
            x1={x(
              fit.minX,
            )}
            y1={y(
              fit.intercept +
                fit.slope *
                  fit.minX,
            )}
            x2={x(
              fit.maxX,
            )}
            y2={y(
              fit.intercept +
                fit.slope *
                  fit.maxX,
            )}
            stroke="#b64f35"
            strokeWidth={2}
          />
        )}

        {points.map(
          (
            point,
            index,
          ) => {
            const selected =
              selectedKeys.has(
                `${point.date}|${point.t}`,
              );

            return (
              <circle
                key={`${point.date}-${index}`}
                cx={x(
                  point.sqrtT,
                )}
                cy={y(
                  point.inverseNormalizedPseudoPressure,
                )}
                r={
                  selected
                    ? 4.2
                    : 3
                }
                fill={
                  selected
                    ? ETH.text
                    : '#aaa'
                }
              />
            );
          },
        )}

        <text
          x={
            left +
            plotW / 2
          }
          y={height - 8}
          textAnchor="middle"
          fontSize={11}
          fill={
            ETH.textSec
          }
        >
          √t / d¹ᐟ²
        </text>

        <text
          x={18}
          y={
            top +
            plotH / 2
          }
          textAnchor="middle"
          fontSize={11}
          fill={
            ETH.textSec
          }
          transform={`rotate(-90 18 ${
            top +
            plotH / 2
          })`}
        >
          Δm / q
        </text>
      </svg>
    </div>
  );
}

function FitCard({
  title,
  equation,
  fit,
}: {
  title: string;
  equation: string;
  fit:
    RtaLinearFit | null;
}) {
  return (
    <div
      style={{
        padding: 14,
        background:
          ETH.canvasBg,
        border: `1px solid ${ETH.headerBorder}`,
      }}
    >
      <div
        style={{
          fontSize: 14,
          fontWeight: 700,
        }}
      >
        {title}
      </div>

      <div
        style={{
          marginTop: 4,
          fontSize: 10,
          color:
            ETH.textMuted,
          fontFamily:
            ETH.fontMono,
        }}
      >
        {equation}
      </div>

      <div
        style={{
          marginTop: 12,
          display: 'grid',
          gap: 7,
        }}
      >
        <ResultLine
          label="拟合点数"
          value={
            fit
              ? `${fit.count}`
              : '--'
          }
        />

        <ResultLine
          label="斜率"
          value={
            fit
              ? fmt(
                  fit.slope,
                  6,
                )
              : '--'
          }
        />

        <ResultLine
          label="截距"
          value={
            fit
              ? fmt(
                  fit.intercept,
                  6,
                )
              : '--'
          }
        />

        <ResultLine
          label="R²"
          value={
            fit
              ? fmt(
                  fit.r2,
                  4,
                )
              : '--'
          }
          strong
        />

        <ResultLine
          label="RMSE"
          value={
            fit
              ? fmt(
                  fit.rmse,
                  4,
                )
              : '--'
          }
        />
      </div>
    </div>
  );
}

function StatusCard({
  title,
  value,
  description,
}: {
  title: string;
  value: string;
  description: string;
}) {
  return (
    <div
      style={{
        padding: 12,
        border: `1px solid ${ETH.headerBorder}`,
        background:
          ETH.bg,
      }}
    >
      <div
        style={{
          fontSize: 10,
          color:
            ETH.textMuted,
        }}
      >
        {title}
      </div>

      <div
        style={{
          marginTop: 6,
          fontSize: 17,
          fontWeight: 800,
        }}
      >
        {value}
      </div>

      <div
        style={{
          marginTop: 4,
          fontSize: 10,
          color:
            ETH.textMuted,
        }}
      >
        {description}
      </div>
    </div>
  );
}

function ResultLine({
  label,
  value,
  strong = false,
}: {
  label: string;
  value: string;
  strong?: boolean;
}) {
  return (
    <div
      style={{
        display: 'flex',
        justifyContent:
          'space-between',
        gap: 12,
        padding:
          strong
            ? '8px 0'
            : '4px 0',
        borderTop:
          strong
            ? `1px solid ${ETH.headerBorder}`
            : undefined,
      }}
    >
      <span
        style={{
          fontSize: 11,
          color:
            ETH.textMuted,
        }}
      >
        {label}
      </span>

      <span
        style={{
          fontFamily:
            ETH.fontMono,
          fontSize:
            strong
              ? 15
              : 11,
          fontWeight:
            strong
              ? 800
              : 600,
        }}
      >
        {value}
      </span>
    </div>
  );
}

function makeEmptyRows():
  PvtInputRow[] {
  return [
    {
      id: 1,
      pressure: '',
      z: '',
      viscosity: '',
    },
    {
      id: 2,
      pressure: '',
      z: '',
      viscosity: '',
    },
    {
      id: 3,
      pressure: '',
      z: '',
      viscosity: '',
    },
    {
      id: 4,
      pressure: '',
      z: '',
      viscosity: '',
    },
    {
      id: 5,
      pressure: '',
      z: '',
      viscosity: '',
    },
  ];
}

function fmt(
  value: number,
  digits = 4,
) {
  if (
    !Number.isFinite(
      value,
    )
  ) {
    return '--';
  }

  if (
    Math.abs(value) >
      1e7 ||
    (
      Math.abs(value) >
        0 &&
      Math.abs(value) <
        1e-5
    )
  ) {
    return value.toExponential(
      4,
    );
  }

  return value.toFixed(
    digits,
  );
}

const inputStyle:
  React.CSSProperties = {
    width: '100%',
    padding: '6px 8px',
    boxSizing:
      'border-box',
    border: `1px solid ${ETH.headerBorder}`,
    background:
      ETH.bg,
    color:
      ETH.text,
    outline:
      'none',
  };

const buttonStyle:
  React.CSSProperties = {
    padding: '6px 10px',
    border: `1px solid ${ETH.headerBorder}`,
    background:
      ETH.bg,
    color:
      ETH.textSec,
    cursor:
      'pointer',
  };

const textButtonStyle:
  React.CSSProperties = {
    border: 'none',
    background:
      'transparent',
    color:
      ETH.textMuted,
    cursor:
      'pointer',
  };

const thStyle:
  React.CSSProperties = {
    padding: '8px',
    borderBottom: `1px solid ${ETH.headerBorder}`,
    textAlign:
      'center',
    color:
      ETH.textSec,
    whiteSpace:
      'nowrap',
  };

const tdStyle:
  React.CSSProperties = {
    padding: '6px 8px',
    borderBottom: `1px solid ${ETH.headerBorder}`,
  };

const numberTdStyle:
  React.CSSProperties = {
    ...tdStyle,
    textAlign:
      'right',
    fontFamily:
      ETH.fontMono,
  };